/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetRemoteMcpServerScopesRequest,
  GetRemoteMcpServerScopesSecurity,
} from "../models/operations/getremotemcpserverscopes.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetRemoteMcpServerScopesQuery,
  GetRemoteMcpServerScopesQueryData,
  prefetchGetRemoteMcpServerScopes,
  queryKeyGetRemoteMcpServerScopes,
} from "./getRemoteMcpServerScopes.core.js";
export {
  buildGetRemoteMcpServerScopesQuery,
  type GetRemoteMcpServerScopesQueryData,
  prefetchGetRemoteMcpServerScopes,
  queryKeyGetRemoteMcpServerScopes,
};

export type GetRemoteMcpServerScopesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getServerScopes remoteMcp
 *
 * @remarks
 * Report the scope state of the protected resource a remote-backed MCP server's logins are for: the operator pin, which belongs to the protected resource and so is shared by every server in the project with the same upstream URL, the scopes the resource advertises and last challenged with, the organization's resource scope discovery flag, and what a login through each bound client would request now. Requires read access to the server only. Reads cached state only; never contacts the resource.
 */
export function useGetRemoteMcpServerScopes(
  request: GetRemoteMcpServerScopesRequest,
  security?: GetRemoteMcpServerScopesSecurity | undefined,
  options?: QueryHookOptions<
    GetRemoteMcpServerScopesQueryData,
    GetRemoteMcpServerScopesQueryError
  >,
): UseQueryResult<
  GetRemoteMcpServerScopesQueryData,
  GetRemoteMcpServerScopesQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildGetRemoteMcpServerScopesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getServerScopes remoteMcp
 *
 * @remarks
 * Report the scope state of the protected resource a remote-backed MCP server's logins are for: the operator pin, which belongs to the protected resource and so is shared by every server in the project with the same upstream URL, the scopes the resource advertises and last challenged with, the organization's resource scope discovery flag, and what a login through each bound client would request now. Requires read access to the server only. Reads cached state only; never contacts the resource.
 */
export function useGetRemoteMcpServerScopesSuspense(
  request: GetRemoteMcpServerScopesRequest,
  security?: GetRemoteMcpServerScopesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetRemoteMcpServerScopesQueryData,
    GetRemoteMcpServerScopesQueryError
  >,
): UseSuspenseQueryResult<
  GetRemoteMcpServerScopesQueryData,
  GetRemoteMcpServerScopesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetRemoteMcpServerScopesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetRemoteMcpServerScopesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      mcpServerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: GetRemoteMcpServerScopesQueryData,
): GetRemoteMcpServerScopesQueryData | undefined {
  const key = queryKeyGetRemoteMcpServerScopes(...queryKeyBase);

  return client.setQueryData<GetRemoteMcpServerScopesQueryData>(key, data);
}

export function invalidateGetRemoteMcpServerScopes(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      mcpServerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteMcp", "getServerScopes", ...queryKeyBase],
  });
}

export function invalidateAllGetRemoteMcpServerScopes(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteMcp", "getServerScopes"],
  });
}
